import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { BOOK_DEMO_URL, MaskLine, EASE } from "../../lib/motion";

const Hero = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.muted = true;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-36 md:pt-44 lg:pt-48 pb-20 md:pb-28 overflow-hidden min-h-[88vh] flex items-center"
    >
      {/* ambient brand video behind the headline */}
      <div className="absolute inset-0 overflow-hidden">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="hero-video"
        >
          <source src="/assets/jsc-hero.webm" type="video/webm" />
          <source src="/assets/jsc-hero-h264.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-[#FAF8F5]/97 via-[#FAF8F5]/82 to-[#FAF8F5]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FAF8F5] via-[#FAF8F5]/20 to-[#FAF8F5]/35" />
      </div>

      {/* faint gridline motif */}
      <div className="jsc-gridlines absolute inset-0 opacity-30 [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]" />

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
              className="font-body text-xs md:text-sm uppercase tracking-[0.24em] text-[#1C1C1E] mb-8 flex items-center gap-3"
              data-testid="hero-eyebrow"
            >
              <span className="inline-block w-8 h-px bg-[#B39B77]" />
              Business Intelligence for Fashion Retail
            </motion.p>

            <h1
              data-testid="hero-headline"
              className="font-display font-semibold text-[#1C1C1E] text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.92] tracking-tight"
            >
              <MaskLine delay={0.35}>Your business</MaskLine>
              <MaskLine delay={0.45}>is talking.</MaskLine>
              <MaskLine delay={0.6} className="italic text-[#B39B77]">
                We help you
              </MaskLine>
              <MaskLine delay={0.7} className="text-[#B39B77]">
                listen.
              </MaskLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 1 }}
              className="mt-8 max-w-xl font-body text-lg md:text-xl leading-relaxed text-[#1C1C1E]"
              data-testid="hero-subheading"
            >
              JSC turns your sales, inventory and product data into clear, confident
              decisions — so you stop guessing and start knowing what your store is telling you.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 1.15 }}
              className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <a
                href={BOOK_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-demo"
                className="group inline-flex items-center gap-2 rounded-full bg-[#B39B77] px-7 py-4 font-body text-base font-medium text-[#1C1C1E] transition-colors duration-300 hover:bg-[#96805E]"
              >
                Book a Demo
                <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <Link
                to="/foresightflow"
                data-testid="hero-explore-foresightflow"
                className="group inline-flex items-center gap-2 rounded-full border border-[#1C1C1E]/25 px-7 py-4 font-body text-base font-medium text-[#1C1C1E] transition-colors duration-300 hover:border-[#1C1C1E]"
              >
                Explore ForesightFlow
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>

          {/* Right column intentionally left open so the ambient video is fully visible */}
        </div>
      </div>
    </section>
  );
};

export default Hero;
